import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/affiliate-scoped.css";

// Icons
import { 
  FaUsers, 
  FaRocket, 
  FaTrophy,
  FaDollarSign,
  FaLink,
  FaRupeeSign,
  FaUniversity,
  FaCheck,
  FaUserPlus,
  FaClock
} from "react-icons/fa";

const Affiliate = () => {
  const stats = [
    { number: "30%", label: "Commission Rate" },
    { number: "₹500", label: "Minimum Payout" },
    { number: "30 Days", label: "Payment Time" },
    { number: "24/7", label: "Support Available" }
  ];

  const steps = [
    {
      icon: FaUserPlus,
      title: "Join Our Program",
      description: "Sign up for our affiliate program and get your unique referral link to start promoting ClubHosty services."
    },
    {
      icon: FaLink,
      title: "Share Your Link", 
      description: "Share your affiliate link with friends, family, or your audience through social media, websites, or direct communication."
    },
    {
      icon: FaDollarSign,
      title: "Earn Commissions",
      description: "Earn 30% commission on every successful sale made through your referral link. No limits on earnings!"
    }
  ];

  const commissionFeatures = [
    "30% commission on all hosting plans",
    "Real-time tracking dashboard",
    "Multiple payment methods available", 
    "No earning limits or caps",
    "Recurring commissions on renewals",
    "Dedicated affiliate support team"
  ];

  const terms = [
    "Accounts with fraudulent, abusive or illegal activities will not be considered as referrals and will not be paid.",
    "Accounts have to remain active and be in good standing for at least 60 days to be paid.",
    "Referrals which request refund for any reason will not be paid.",
    "Currently Affiliate Commission only available with ClubHosty Shared Hosting, Cpanel Hosting and Reseller Plans.",
    "No unsolicited bulk e-mail traffic, no blind redirects, or other malicious advertising like coupon, discount code will not be accepted.",
    "Affiliate accounts which are in violation of any rule or are abusing the affiliate program in any way, will be terminated with no prior notice.",
    "We reserve the right to cancel or modify this affiliate program at any time and for any reason."
  ];

  return (
    <div className="affiliate-page-wrapper">
      <Helmet>
        <title>Highest Paying Web Hosting Affiliate Program by ClubHosty</title>
        <meta
          name="description"
          content="Join Highest Paying Web Hosting Affiliate Program by ClubHosty and promote top-quality web hosting services and start earning big today!"
        />
        <link rel="canonical" href="https://www.clubhosty.com/affiliate" />
      </Helmet>

      {/* Hero Section */}
      <section className="affiliate-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaTrophy />
              <span>Highest Paying Affiliate Program</span>
            </div>
            <h1 className="hero-title">
              Refer and <span className="text-gradient">Earn Big</span> with ClubHosty
            </h1>
            <p className="hero-description">
              Join our lucrative affiliate program and earn 30% commission on every sale. 
              Start monetizing your network and build a passive income stream with India's 
              most trusted hosting provider.
            </p>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* How it Works Section */}
        <section className="how-it-works-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">How It Works</div>
              <h2>Start Earning in 3 Simple Steps</h2>
              <p>
                Our affiliate program is designed to be simple and rewarding. 
                Follow these easy steps to start earning commissions today.
              </p>
            </div>
            
            <div className="row">
              {steps.map((step, index) => (
                <div key={index} className="col-lg-4 col-md-6 mb-4">
                  <div className="step-card">
                    <div className="step-number">{index + 1}</div>
                    <div className="step-icon">
                      <step.icon />
                    </div>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Commission Structure Section */}
        <section className="commission-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Commission Structure</div>
              <h2>Earn More with Every Referral</h2>
              <p>
                Our competitive commission structure ensures you get rewarded 
                generously for every successful referral you bring to ClubHosty.
              </p>
            </div>
            
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="commission-card">
                  <div className="commission-header">
                    <div className="commission-rate">30%</div>
                    <div className="commission-title">Commission Rate</div>
                    <div className="commission-subtitle">On Every Sale</div>
                  </div>
                  <div className="commission-body">
                    <ul className="commission-features">
                      {commissionFeatures.map((feature, index) => (
                        <li key={index}>
                          <div className="feature-icon">
                            <FaCheck />
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="row mt-4">
                      <div className="col-md-4 text-center mb-3">
                        <div className="step-icon mx-auto mb-2" style={{width: '60px', height: '60px'}}>
                          <FaRupeeSign />
                        </div>
                        <h6>Minimum Payout</h6>
                        <p className="text-muted">₹500</p>
                      </div>
                      <div className="col-md-4 text-center mb-3">
                        <div className="step-icon mx-auto mb-2" style={{width: '60px', height: '60px'}}>
                          <FaClock />
                        </div>
                        <h6>Payment Time</h6>
                        <p className="text-muted">Within 30 Days</p>
                      </div>
                      <div className="col-md-4 text-center mb-3">
                        <div className="step-icon mx-auto mb-2" style={{width: '60px', height: '60px'}}>
                          <FaUniversity />
                        </div>
                        <h6>Payment Methods</h6>
                        <p className="text-muted">UPI, Bank, PayPal</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Terms & Conditions Section */}
        <section className="terms-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Terms & Conditions</div>
              <h2>Program Guidelines</h2>
              <p>
                Please review these important terms and conditions to ensure 
                compliance with our affiliate program requirements.
              </p>
            </div>
            
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="terms-card">
                  <ul className="terms-list">
                    {terms.map((term, index) => (
                      <li key={index}>
                        <div className="term-number">{index + 1}</div>
                        <div className="term-content">{term}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Start Earning?</h2>
              <p className="cta-description">
                Join thousands of affiliates who are already earning with ClubHosty. 
                Start promoting today and watch your income grow!
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to="/contact" className="btn-cta">
                  <span>Join Affiliate Program</span>
                  <FaRocket />
                </Link>
                <Link to="/contact" className="btn-cta">
                  <span>Contact Support</span>
                  <FaUsers />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Affiliate;
